import { Currency } from '@/modules/common';
import { DEFAULT_CURRENCY_CODE } from '@/modules/common/constants/currency.constants';
import { QuickBooksWarning } from '@/modules/finance/common';
import { hasQuickBooksIntegration } from '@/modules/finance/common/utils/generate-entities.utils';
import { ShortProjectResponse } from '@/modules/projects/types/dto/response/short-project-response';

import { GenerateInvoiceListItem } from '../types';
import { QuickBooksProjectWarningTypeDTO } from '../types/dto/response/quick-books-project-warning.response';

/**
 * A project added by hand never went through the generate endpoint, so it carries no server
 * verdict. The missing QuickBooks link is the one warning derivable client-side, and reproducing
 * it keeps an unlinked project out of the selection exactly as a server-loaded one would be.
 */
const buildProjectQuickBooksWarnings = (
  project: ShortProjectResponse
): QuickBooksWarning<QuickBooksProjectWarningTypeDTO>[] =>
  hasQuickBooksIntegration(project.connectedIntegrations)
    ? []
    : [{ type: QuickBooksProjectWarningTypeDTO.HAS_NO_QUICK_BOOKS_INTEGRATION }];

/**
 * Builds the blank invoice row for a project the user added from the "add custom project" picker.
 *
 * Every billable figure starts at zero: the project has no tracked work in the period (that is
 * why it is absent from the generated list), so the row exists purely to carry the custom lines
 * the user is about to add to it. Sub-rows — the added members, the ad-hoc overheads and the
 * "add line" button — are composed by the page alongside the server projects, so both kinds of
 * row gain and lose lines through the same code path.
 */
export const mapShortProjectToCustomInvoiceRow = (
  project: ShortProjectResponse
): GenerateInvoiceListItem => {
  const warnings = buildProjectQuickBooksWarnings(project);
  const currencyCode = project.currencyCode ?? DEFAULT_CURRENCY_CODE;

  return {
    id: String(project.id),
    invoiceBy: '',
    hours: 0,
    effectiveRate: 0,
    currency: {
      code: currencyCode,
      displayName: '',
    } as Currency,
    total: 0,
    project: {
      id: project.id,
      colorHex: project.colorHex ?? '',
      name: project.name,
      logoUrl: project.logoUrl,
      clientName: project.clientName ?? null,
      billingModel: project.billingModel ?? null,
      warnings,
    },
    projectMembers: [],
    projectAddedMembers: [],
    projectOverheads: [],
    projectSubRows: [],
  };
};
